import { useState } from 'react';
import { Search, Code2, Settings, Loader2, Network } from 'lucide-react';
import { useKnowledgeGraph } from '@/lib/hooks/useKnowledgeGraph';
import { KnowledgeGraphVisualization } from '@/components/KnowledgeGraphVisualization';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorMessage } from '@/components/ErrorMessage';
import type { KnowledgeGraphParams } from '@/types/knowledgeGraph';

export function KnowledgeGraphPage() {
  const [searchQuery, setSearchQuery] = useState('x402');
  const [activeQuery, setActiveQuery] = useState('x402');
  const [viewMode, setViewMode] = useState<'graph' | 'json'>('graph');
  const [showSettings, setShowSettings] = useState(false);

  // Graph parameters
  const [params, setParams] = useState<KnowledgeGraphParams>({
    query: 'x402',
    limit: 5,
    depth: 2,
    max_nodes: 100,
    max_files_per_repo: 10,
    max_concepts_per_file: 5,
    min_stars: 50,
  });

  const { data, isLoading, error, refetch } = useKnowledgeGraph(params);

  const handleSearch = () => {
    setParams(prev => ({ ...prev, query: searchQuery }));
    setActiveQuery(searchQuery);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search Query
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g., ERC20, DeFi lending, NFT marketplace..."
                  className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={isLoading || !searchQuery}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Network className="h-4 w-4" />
                  Generate Graph
                </>
              )}
            </button>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Repositories
                </label>
                <input
                  type="number"
                  value={params.limit}
                  onChange={(e) => setParams(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
                  min={1}
                  max={20}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Depth
                </label>
                <select
                  value={params.depth}
                  onChange={(e) => setParams(prev => ({ ...prev, depth: parseInt(e.target.value) }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                >
                  <option value={1}>1 (Repos only)</option>
                  <option value={2}>2 (+ Files & Concepts)</option>
                  <option value={3}>3 (+ Functions)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Max Nodes
                </label>
                <input
                  type="number"
                  value={params.max_nodes}
                  onChange={(e) => setParams(prev => ({ ...prev, max_nodes: parseInt(e.target.value) }))}
                  min={10}
                  max={200}
                  step={10}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Files per Repo
                </label>
                <input
                  type="number"
                  value={params.max_files_per_repo}
                  onChange={(e) => setParams(prev => ({ ...prev, max_files_per_repo: parseInt(e.target.value) }))}
                  min={1}
                  max={50}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Concepts per File
                </label>
                <input
                  type="number"
                  value={params.max_concepts_per_file}
                  onChange={(e) => setParams(prev => ({ ...prev, max_concepts_per_file: parseInt(e.target.value) }))}
                  min={1}
                  max={20}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Stars
                </label>
                <input
                  type="number"
                  value={params.min_stars}
                  onChange={(e) => setParams(prev => ({ ...prev, min_stars: parseInt(e.target.value) }))}
                  min={0}
                  max={10000}
                  step={50}
                  className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {error && (
          <ErrorMessage
            error={error instanceof Error ? error.message : 'Failed to generate knowledge graph'}
          />
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* Stats Bar */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-gray-500">Query</p>
                    <p className="text-sm font-medium text-gray-900">{activeQuery}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Nodes</p>
                    <p className="text-sm font-medium text-gray-900">{data.stats.total_nodes}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Edges</p>
                    <p className="text-sm font-medium text-gray-900">{data.stats.total_edges}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Repositories</p>
                    <p className="text-sm font-medium text-gray-900">{data.stats.node_types.repository || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Files</p>
                    <p className="text-sm font-medium text-gray-900">{data.stats.node_types.file || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Concepts</p>
                    <p className="text-sm font-medium text-gray-900">{data.stats.node_types.concept || 0}</p>
                  </div>
                  {data.metadata.pruned && (
                    <div className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                      Pruned
                    </div>
                  )}
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('graph')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      viewMode === 'graph'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Network className="h-4 w-4" />
                    Graph
                  </button>
                  <button
                    onClick={() => setViewMode('json')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      viewMode === 'json'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Code2 className="h-4 w-4" />
                    JSON
                  </button>
                </div>
              </div>
            </div>

            {/* Graph Visualization or JSON View */}
            {viewMode === 'graph' ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden p-4">
                <div style={{ height: '800px' }}>
                  <KnowledgeGraphVisualization graph={data} />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <pre className="text-xs overflow-auto max-h-[700px] bg-gray-900 text-gray-100 p-4 rounded-lg">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
